import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, Settings, DollarSign, 
  ToggleLeft, ToggleRight, RefreshCw,
  CheckCircle, XCircle, AlertCircle, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientManagementPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Global pricing
  const [globalPricing, setGlobalPricing] = useState({
    cold_call_price: 5.00,
    live_transfer_price: 25.00,
    data_lead_price: 2.00
  });
  
  // Top-up dialog
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNotes, setTopUpNotes] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchGlobalPricing();
  }, []);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const response = await axios.get(`${API_URL}/api/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalPricing = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const response = await axios.get(`${API_URL}/api/admin/clients/pricing/global`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch global pricing');
    }
  };
      fetchClients();
  const handleTopUp = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.post(`${API_URL}/api/admin/clients/${selectedClient.id}/balance/topup`, {
        amount: parseFloat(topUpAmount),
        payment_method: 'admin',
        notes: topUpNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Balance added successfully');
      setIsTopUpOpen(false);
      setTopUpAmount('');
      setTopUpNotes('');
      fetchClients();
    } catch (error) {
      toast.error('Failed to add balance');
    }
  };

  const handleUpdateGlobalPricing = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(`${API_URL}/api/admin/clients/pricing/global`, globalPricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Global pricing updated');
    } catch (error) {
      toast.error('Failed to update pricing');
    }
  };

  const handleToggleStatus = async (client) => {
    try {
      const token = localStorage.getItem('dme_token');
      const newStatus = client.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${API_URL}/api/admin/clients/${client.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Client ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchClients();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleImpersonate = async (client) => {
    try {
      const token = localStorage.getItem('dme_token');
      const response = await axios.post(`${API_URL}/api/admin/clients/${client.id}/impersonate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store client token and impersonation info
      localStorage.setItem('client_token', response.data.access_token);
      localStorage.setItem('client_info', JSON.stringify(response.data.client));
      localStorage.setItem('is_impersonation', 'true');
      localStorage.setItem('impersonated_by', response.data.impersonated_by);
      
      toast.success(`Impersonating ${client.company_name}`);
      
      // Open client portal in new tab
      window.open('/client-portal/dashboard', '_blank');
    } catch (error) {
      toast.error('Failed to impersonate client');
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
      case 'suspended':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Client Portal Management
          </h1>
          <p className="text-muted-foreground">
            Manage lead vendor clients, settings, and pricing
          </p>
        </div>
        <Button onClick={() => navigate('/client-management/add')} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="clients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="pricing">Global Pricing</TabsTrigger>
        </TabsList>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchClients}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Clients Table */}
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Available</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Total Leads</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No clients found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredClients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell className="font-medium">{client.company_name}</TableCell>
                        <TableCell>
                          <div>
                            <div>{client.contact_name}</div>
                            <div className="text-xs text-muted-foreground">{client.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(client.status)}</TableCell>
                        <TableCell>
                          {client.is_available ? (
                            <Badge className="bg-green-500">Available</Badge>
                          ) : (
                            <Badge variant="outline">Unavailable</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${client.balance?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell className="text-right">
                          {client.total_leads_received || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleImpersonate(client)}
                              title="Impersonate Client"
                              data-testid={`impersonate-${client.id}`}
                            >
                              <UserCheck className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedClient(client);
                                setIsTopUpOpen(true);
                              }}
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/client-management/${client.id}`)}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(client)}
                            >
                              {client.status === 'active' ? (
                                <ToggleRight className="w-4 h-4 text-green-500" />
                              ) : (
                                <ToggleLeft className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Pricing Tab */}
        <TabsContent value="pricing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Global Lead Pricing
              </CardTitle>
              <CardDescription>
                Default pricing for all clients. Can be overridden per-client in their settings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Cold Call Lead Price</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={globalPricing.cold_call_price}
                      onChange={(e) => setGlobalPricing({...globalPricing, cold_call_price: parseFloat(e.target.value) || 0})}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Price charged per cold call lead</p>
                </div>
                <div className="space-y-2">
                  <Label>Live Transfer Lead Price</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={globalPricing.live_transfer_price}
                      onChange={(e) => setGlobalPricing({...globalPricing, live_transfer_price: parseFloat(e.target.value) || 0})}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Price charged per live transfer</p>
                </div>
                <div className="space-y-2">
                  <Label>Data Lead Price</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={globalPricing.data_lead_price}
                      onChange={(e) => setGlobalPricing({...globalPricing, data_lead_price: parseFloat(e.target.value) || 0})}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Price charged per data lead</p>
                </div>
              </div>
              <Button onClick={handleUpdateGlobalPricing}>
                Save Global Pricing
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Top-Up Dialog */}
      <Dialog open={isTopUpOpen} onOpenChange={setIsTopUpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Balance: {selectedClient?.company_name}</DialogTitle>
            <DialogDescription>
              Current balance: ${selectedClient?.balance?.toFixed(2) || '0.00'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount to Add</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="pl-9"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={topUpNotes}
                onChange={(e) => setTopUpNotes(e.target.value)}
                placeholder="Payment reference, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTopUpOpen(false)}>Cancel</Button>
            <Button onClick={handleTopUp} disabled={!topUpAmount || parseFloat(topUpAmount) <= 0}>
              Add ${topUpAmount || '0.00'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
